import { a as __rest } from '../../../tslib.es6.esm.js';
import { jsx } from 'react/jsx-runtime';
import * as React from 'react';
import { useState, useRef, useCallback, useEffect } from 'react';
import * as RadixSlider from '@radix-ui/react-slider';
import { useSliderA11y } from '../context/SliderA11yProvider.esm.js';
import { useFocusVisibleMode } from '../hooks/useFocusVisibleMode.esm.js';
import sliderThumbStyles from './SliderThumb.styles.esm.js';
import { cn } from '../../../utils/cn.esm.js';

const SliderThumb = React.forwardRef((_a, ref) => {
    var { className, inverse = false, onFocus, onBlur, min, max, value, thumbType = "single" } = _a, props = __rest(_a, ["className", "inverse", "onFocus", "onBlur", "min", "max", "value", "thumbType"]);
    const { setIsSliderFocused, focusPrevInputFor, setLowerThumbRef, setUpperThumbRef, focusLowerThumb, } = useSliderA11y();
    const hadKeyboardEvent = useFocusVisibleMode();
    const [isFocused, setIsFocused] = useState(false);
    const keyboardFocusPersistRef = useRef(false);
    const setMergedRef = useCallback((node) => {
        if (thumbType === "lower")
            setLowerThumbRef(node);
        if (thumbType === "upper")
            setUpperThumbRef(node);
        if (typeof ref === "function")
            ref(node);
        else if (ref)
            ref.current =
                node;
    }, [ref, thumbType, setLowerThumbRef, setUpperThumbRef]);
    useEffect(() => {
        if (isFocused && hadKeyboardEvent)
            keyboardFocusPersistRef.current = true;
        if (!isFocused)
            keyboardFocusPersistRef.current = false;
    }, [isFocused, hadKeyboardEvent]);
    const handleFocus = (e) => {
        setIsFocused(true);
        setIsSliderFocused(true);
        onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
    };
    const handleBlur = (e) => {
        setIsFocused(false);
        setIsSliderFocused(false);
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
    };
    const handleKeyDown = (e) => {
        if (e.key === "Tab" && e.shiftKey) {
            e.preventDefault();
            if (thumbType === "upper") {
                focusLowerThumb();
                return;
            }
            focusPrevInputFor(thumbType);
        }
    };
    const showRing = isFocused && (hadKeyboardEvent || keyboardFocusPersistRef.current);
    const thumbClass = sliderThumbStyles({
        inverse,
        focusRing: showRing,
        inverseFocusRing: inverse && showRing,
    });
    const ariaValueText = `Minimum value ${min} and maximum value ${max}, current value ${value}`;
    return (jsx(RadixSlider.Thumb, Object.assign({ ref: setMergedRef, className: cn(thumbClass, className), onFocus: handleFocus, onBlur: handleBlur, onKeyDown: handleKeyDown, "aria-label": "", "aria-valuetext": ariaValueText }, props)));
});
SliderThumb.displayName = "SliderThumb";

export { SliderThumb };
